import React, { useEffect, useRef, useState } from "react";

import { Box, Tooltip } from "@mui/material";

import styles from "./topFilter.module.css";
import GenericFilterItem from "../GenericFilterItem";
import GenericNavItem from "../GenericNavItem";
import { PanelId } from "../../context/PanelContext";
import { Filter, FilterType } from "../../context/ConfigContext/types";
import { format } from "date-fns";
import FilterAltOutlinedIcon from "@mui/icons-material/FilterAltOutlined";
import GenericButton from "../GenericButton";
import { useConfig } from "../../context/ConfigContext";
import { useDocuments } from "../../context/DocumentContext";

interface TopFiltersProps {
	filters: Filter[];
	dateRange: { start: Date; end: Date };
}

const filterIconPath =
	"M10.4 2c1.045 0 1.934.668 2.263 1.6H15.2a.8.8 0 1 1 0 1.6h-2.537a2.401 2.401 0 0 1-4.526 0H.8a.8.8 0 1 1 0-1.6h7.337A2.401 2.401 0 0 1 10.4 2zm0 1.6a.8.8 0 1 0 0 1.6.8.8 0 0 0 0-1.6zM5.6 9.2a2.401 2.401 0 0 0-2.263 1.6H.8a.8.8 0 1 0 0 1.6h2.537a2.401 2.401 0 0 0 4.526 0H15.2a.8.8 0 1 0 0-1.6H7.863A2.401 2.401 0 0 0 5.6 9.2zm0 1.6a.8.8 0 1 1 0 1.6.8.8 0 0 1 0-1.6z";
const calendarIconPath =
	"M9 .675c.275 0 .501.212.523.482l.002.043v.3h.675a1.8 1.8 0 0 1 1.799 1.733L12 3.3V9a1.8 1.8 0 0 1-1.733 1.799l-.067.001H1.8A1.8 1.8 0 0 1 .001 9.067L0 9V3.3a1.8 1.8 0 0 1 1.733-1.799L1.8 1.5h.675v-.3a.525.525 0 0 1 1.048-.043l.002.043v.3h4.95v-.3c0-.29.235-.525.525-.525zM10.2 2.7h-.697a.525.525 0 0 1-1.006 0H3.503a.525.525 0 0 1-1.006 0H1.8a.6.6 0 0 0-.598.555L1.2 3.3V9a.6.6 0 0 0 .555.598L1.8 9.6h8.4a.6.6 0 0 0 .598-.555L10.8 9V3.3a.6.6 0 0 0-.555-.598L10.2 2.7zM3.36 7.74a.24.24 0 0 1 .24.24v.48a.24.24 0 0 1-.24.24h-.48a.24.24 0 0 1-.24-.24v-.48a.24.24 0 0 1 .24-.24h.48zm1.92 0a.24.24 0 0 1 .24.24v.48a.24.24 0 0 1-.24.24H4.8a.24.24 0 0 1-.24-.24v-.48a.24.24 0 0 1 .24-.24h.48zm1.92 0a.24.24 0 0 1 .24.24v.48a.24.24 0 0 1-.24.24h-.48a.24.24 0 0 1-.24-.24v-.48a.24.24 0 0 1 .24-.24h.48zM5.28 5.82a.24.24 0 0 1 .24.24v.48a.24.24 0 0 1-.24.24H4.8a.24.24 0 0 1-.24-.24v-.48a.24.24 0 0 1 .24-.24h.48zm1.92 0a.24.24 0 0 1 .24.24v.48a.24.24 0 0 1-.24.24h-.48a.24.24 0 0 1-.24-.24v-.48a.24.24 0 0 1 .24-.24h.48zm1.92 0a.24.24 0 0 1 .24.24v.48a.24.24 0 0 1-.24.24h-.48a.24.24 0 0 1-.24-.24v-.48a.24.24 0 0 1 .24-.24h.48zm-5.76 0a.24.24 0 0 1 .24.24v.48a.24.24 0 0 1-.24.24h-.48a.24.24 0 0 1-.24-.24v-.48a.24.24 0 0 1 .24-.24h.48zM5.28 3.9a.24.24 0 0 1 .24.24v.48a.24.24 0 0 1-.24.24H4.8a.24.24 0 0 1-.24-.24v-.48a.24.24 0 0 1 .24-.24h.48zm1.92 0a.24.24 0 0 1 .24.24v.48a.24.24 0 0 1-.24.24h-.48a.24.24 0 0 1-.24-.24v-.48a.24.24 0 0 1 .24-.24h.48zm1.92 0a.24.24 0 0 1 .24.24v.48a.24.24 0 0 1-.24.24h-.48a.24.24 0 0 1-.24-.24v-.48a.24.24 0 0 1 .24-.24h.48zm-5.76 0a.24.24 0 0 1 .24.24v.48a.24.24 0 0 1-.24.24h-.48a.24.24 0 0 1-.24-.24v-.48a.24.24 0 0 1 .24-.24h.48z";
const dateFormat = "yyyy-MM-dd";

const TopFilters: React.FunctionComponent<TopFiltersProps> = ({
	filters,
	dateRange,
}) => {
	const { handleApplyFilters } = useDocuments();
	const [disableFilterButton, setDisableFilterButton] = useState(true);
	const [needIncludeText, setNeedsIncludeText] = useState(false);
	const { filters: allFilters, searchQuery, startDate, endDate } = useConfig();
	const firstUpdate = useRef(true);

	useEffect(() => {
		if (firstUpdate.current) {
			firstUpdate.current = false;
			return;
		}

		if (
			(searchQuery.excludeDocuments.length || searchQuery.excludeText) &&
			!searchQuery.includeDocuments.length &&
			!searchQuery.includeText
		) {
			setNeedsIncludeText(true);
			return setDisableFilterButton(true);
		}

		setNeedsIncludeText(false);
		setDisableFilterButton(false);
	}, [allFilters, startDate, endDate, searchQuery]);

	const handleFilterButtonClick = () => {
		setDisableFilterButton(true);
		handleApplyFilters();
	};

	const applicableFilters = allFilters.filter(
		(f) => f.type === FilterType.CATEGORY || f.type === FilterType.LANGUAGE
	);

	return (
		<Box className={styles.filterBar}>
			<div className={styles.leftSide}>
				<GenericNavItem iconPath={filterIconPath} panelId={PanelId.Filter} />
				{filters?.map((filter) => (
					<div key={filter.filterName} className={styles.filterItem}>
						<GenericFilterItem filter={filter} />
					</div>
				))}
				<Tooltip
					arrow
					title={
						"Include text or document required in order for the query to work"
					}
					open={needIncludeText}
				>
					<div>
						<GenericButton
							disabled={disableFilterButton}
							onClick={handleFilterButtonClick}
							text={
								applicableFilters.length && filters.length
									? "apply filters"
									: ""
							}
							startIcon={<FilterAltOutlinedIcon />}
						/>
					</div>
				</Tooltip>
			</div>
			<div className={styles.rightSide}>
				<GenericNavItem
					iconPath={calendarIconPath}
					panelId={PanelId.Date}
					width="fit-content"
					text={
						dateRange?.start &&
						dateRange?.end &&
						`${format(dateRange.start, dateFormat)} - ${format(
							dateRange.end,
							dateFormat
						)}`
					}
					transformViewBox="0 -3 16 16"
				/>
			</div>
		</Box>
	);
};

export default TopFilters;
